import React, { useCallback, useEffect, useId, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { DatePicker } from "@/lib/shadcn/components/date-picker";
import { errorPopup } from "@/utils/alerts";
import { dateToString } from "@/utils/periods";
import { formatDateToISODate } from "@/utils/dates";
import { cn } from "@/lib/shadcn/utils";
import { CleaningRate } from "@/store/slices/apartments";

export interface EditCleaningRateDialogProps {
    open: boolean;
    onClose: () => void;
    apartmentName: string;
    apartmentId: number;
    /** The currently-effective rate for the apartment, if any (drives the "Current" section). */
    currentRate?: CleaningRate;
    /** All of the apartment's existing rates, used to detect an add-vs-update for the chosen date. */
    existingRates: CleaningRate[];
    onSubmit: (cleaningFee: number, cleaningCost: number, effectiveFrom: Date, existingRate?: CleaningRate) => void;
}

const toNumber = (value: number | string | undefined): number => {
    if (value === undefined) return 0;
    return typeof value === "number" ? value : parseFloat(value) || 0;
};

const formatMoney = (value: number | string | undefined): string => toNumber(value).toFixed(2);

/**
 * Finds an existing rate whose effective date is the same calendar day as the
 * picked `date`, deciding add-vs-update. Compares ISO date *strings* rather than
 * parsing `effectiveFrom` with `new Date(...)`: the stored value is a date-only
 * (or UTC-timestamped) ISO string, and `new Date("2026-02-01")` resolves to UTC
 * midnight — reading its local Y/M/D back in a UTC-negative timezone shifts the
 * day, which would silently turn an update into a duplicate add. `slice(0, 10)`
 * keeps just the `YYYY-MM-DD`, and `formatDateToISODate` is itself local-Y/M/D
 * based, so the comparison is timezone-proof. Exported for unit testing.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function findRateForEffectiveDate(rates: CleaningRate[], date: Date): CleaningRate | undefined {
    const target = formatDateToISODate(date);
    return rates.find((rate) => (rate.effectiveFrom ?? "").slice(0, 10) === target);
}

/**
 * Add/edit dialog for per-apartment, effective-dated cleaning rates. Mirrors
 * `EditRatioDialog`, but cleaning rates carry two monetary fields (fee + cost)
 * and an effective date that may be ANY calendar day — so it uses the shared
 * `DatePicker` rather than the ratio dialog's month/year selects.
 */
export const EditCleaningRateDialog: React.FC<EditCleaningRateDialogProps> = ({
    open,
    onClose,
    apartmentName,
    apartmentId,
    currentRate,
    existingRates,
    onSubmit,
}) => {
    const [cleaningFee, setCleaningFee] = useState<number>(toNumber(currentRate?.cleaningFee));
    const [cleaningCost, setCleaningCost] = useState<number>(toNumber(currentRate?.cleaningCost));
    const [isFeeValid, setIsFeeValid] = useState(true);
    const [isCostValid, setIsCostValid] = useState(true);
    const [effectiveFrom, setEffectiveFrom] = useState<Date>(new Date());

    const feeId = useId();
    const costId = useId();
    const effectiveDateGroupId = useId();

    useEffect(() => {
        if (open) {
            setCleaningFee(toNumber(currentRate?.cleaningFee));
            setCleaningCost(toNumber(currentRate?.cleaningCost));
            setEffectiveFrom(new Date());
            setIsFeeValid(true);
            setIsCostValid(true);
        }
    }, [open, currentRate, apartmentId]);

    const getExistingRate = useCallback(
        () => findRateForEffectiveDate(existingRates, effectiveFrom),
        [existingRates, effectiveFrom],
    );

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const existingRate = getExistingRate();
            onSubmit(cleaningFee, cleaningCost, effectiveFrom, existingRate);
            handleClose();
        } catch (error) {
            errorPopup(`Failed to save cleaning rate: ${error}`);
        }
    };

    const handleClose = () => {
        setIsFeeValid(true);
        setIsCostValid(true);
        onClose();
    };

    const validateAndSetFee = useCallback((inputValue: string) => {
        const value = Number(inputValue);
        setCleaningFee(value);
        setIsFeeValid(!Number.isNaN(value) && value >= 0);
    }, []);

    const validateAndSetCost = useCallback((inputValue: string) => {
        const value = Number(inputValue);
        setCleaningCost(value);
        setIsCostValid(!Number.isNaN(value) && value >= 0);
    }, []);

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{apartmentName}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Current Values Section — read-only display, not focusable form fields. */}
                    <div className="space-y-4">
                        <h3 className="text-center text-sm font-medium">Current Cleaning Rate</h3>

                        <dl className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                                <dt className="text-sm font-medium leading-none">Fee (€)</dt>
                                <dd className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm">
                                    {formatMoney(currentRate?.cleaningFee)}
                                </dd>
                            </div>
                            <div className="space-y-2">
                                <dt className="text-sm font-medium leading-none">Cost (€)</dt>
                                <dd className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm">
                                    {formatMoney(currentRate?.cleaningCost)}
                                </dd>
                            </div>
                        </dl>

                        <dl className="space-y-2">
                            <dt className="text-sm font-medium leading-none">Effective From</dt>
                            <dd className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm">
                                {currentRate ? dateToString(new Date(currentRate.effectiveFrom)) : "—"}
                            </dd>
                        </dl>
                    </div>

                    <div className="h-px bg-gray-200" />

                    {/* New Values Section */}
                    <div className="space-y-4">
                        <h3 className="text-center text-sm font-medium">New Cleaning Rate</h3>

                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                                <Label htmlFor={feeId}>
                                    Fee (€) <span aria-hidden="true">*</span>
                                </Label>
                                <Input
                                    id={feeId}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={cleaningFee}
                                    onChange={(e) => validateAndSetFee(e.target.value)}
                                    className={cn(!isFeeValid && "border-red-500 focus-visible:ring-red-500")}
                                    required
                                    aria-required="true"
                                    aria-invalid={!isFeeValid}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor={costId}>
                                    Cost (€) <span aria-hidden="true">*</span>
                                </Label>
                                <Input
                                    id={costId}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={cleaningCost}
                                    onChange={(e) => validateAndSetCost(e.target.value)}
                                    className={cn(!isCostValid && "border-red-500 focus-visible:ring-red-500")}
                                    required
                                    aria-required="true"
                                    aria-invalid={!isCostValid}
                                />
                            </div>
                        </div>

                        <div className="space-y-2" role="group" aria-labelledby={effectiveDateGroupId}>
                            <Label id={effectiveDateGroupId}>Effective From</Label>
                            <DatePicker
                                variant="outline"
                                selectedValue={effectiveFrom}
                                onChange={setEffectiveFrom}
                                ariaLabel="Effective from date"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="default" disabled={!isFeeValid || !isCostValid}>
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
